import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Handshake, ArrowRight, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { api, formatMoney } from '@/lib/api';
import { useI18n } from '@/context/I18nContext';
import { useAuth } from '@/context/AuthContext';

const STATUS = { pending: 'bg-amber-100 text-amber-800', accepted: 'bg-emerald-100 text-emerald-800', rejected: 'bg-rose-100 text-rose-800', expired: 'bg-slate-100 text-slate-700', paid: 'bg-emerald-100 text-emerald-800' };

export default function OffersPage() {
    const { t } = useI18n();
    const { user } = useAuth();
    const [tab, setTab] = useState('all');
    const [items, setItems] = useState([]);
    const [counterAmount, setCounterAmount] = useState({});

    const load = useCallback(async () => {
        const { data } = await api.get('/me/offers', { params: { role: tab } });
        setItems(data || []);
    }, [tab]);
    useEffect(() => { load(); }, [load]);

    const doCounter = async (id) => {
        const val = parseFloat(counterAmount[id] || 0);
        if (!(val > 0)) { toast.error('Monto inválido'); return; }
        try { await api.post(`/offers/${id}/counter`, { amount: val }); toast.success('Contraoferta enviada'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };
    const accept = async (id) => { try { await api.post(`/offers/${id}/accept`); toast.success('Oferta aceptada'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } };
    const reject = async (id) => { try { await api.post(`/offers/${id}/reject`); toast.success('Oferta rechazada'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } };

    return (
        <div className="space-y-4" data-testid="offers-page">
            <div className="flex items-center gap-2">
                <Handshake className="h-6 w-6 text-red-600" />
                <h1 className="text-2xl font-semibold">{t('offers.title','Ofertas')}</h1>
            </div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="all" data-testid="offers-tab-all">Todas</TabsTrigger>
                    <TabsTrigger value="buyer" data-testid="offers-tab-buyer">Como comprador</TabsTrigger>
                    <TabsTrigger value="seller" data-testid="offers-tab-seller">Como vendedor</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-4 space-y-3">
                    {items.length === 0 ? <div className="text-sm text-muted-foreground text-center py-10">{t('common.empty','Sin resultados')}</div> : null}
                    {items.map(o => {
                        const isSeller = o.seller_id === user?.id;
                        const canRespond = o.status === 'pending' && ((isSeller && o.current_by === 'buyer') || (!isSeller && o.current_by === 'seller'));
                        return (
                            <Card key={o.id} data-testid={`offer-${o.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex flex-wrap items-center gap-3 justify-between">
                                        <div>
                                            <div className="text-xs uppercase text-muted-foreground">{isSeller ? 'Recibida de' : 'Enviada a'} @{o.counterpart?.nickname}</div>
                                            <Link to={`/ad/${o.ad_id}`} className="font-semibold hover:text-red-600">{o.ad_title}</Link>
                                            <div className="text-sm text-muted-foreground">Último movimiento: <strong>{o.current_by === 'buyer' ? 'comprador' : 'vendedor'}</strong></div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-semibold text-red-600 tabular-nums">{formatMoney(o.current_amount)}</div>
                                            <Badge className={STATUS[o.status] || ''}>{t(`offers.${o.status}`, o.status)}</Badge>
                                        </div>
                                    </div>
                                    {canRespond && (
                                        <div className="mt-3 flex flex-wrap gap-2 items-center">
                                            <Input type="number" placeholder="Contraoferta" value={counterAmount[o.id] || ''} onChange={e => setCounterAmount(v => ({ ...v, [o.id]: e.target.value }))} className="w-40" data-testid={`offer-counter-input-${o.id}`} />
                                            <Button size="sm" variant="outline" onClick={() => doCounter(o.id)} data-testid={`offer-counter-${o.id}`}><RefreshCw className="h-4 w-4 mr-1" />{t('action.counter','Contraoferta')}</Button>
                                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => accept(o.id)} data-testid={`offer-accept-${o.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />{t('action.accept','Aceptar')}</Button>
                                            <Button size="sm" variant="outline" onClick={() => reject(o.id)} data-testid={`offer-reject-${o.id}`}><XCircle className="h-4 w-4 mr-1" />{t('action.reject','Rechazar')}</Button>
                                        </div>
                                    )}
                                    {o.status === 'accepted' && !isSeller && (
                                        <div className="mt-3">
                                            <Link to={`/ad/${o.ad_id}`} className="text-sm text-red-600 underline inline-flex items-center gap-1">Ir al checkout <ArrowRight className="h-3 w-3" /></Link>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </TabsContent>
            </Tabs>
        </div>
    );
}
